import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IDLE_WARNING_SECONDS } from '@shared/constants/config';
import { IDLE_TIMEOUT_MS, getIdleMs, markActivity } from '@shared/lib/idleActivity';

// Real user input. `scroll` does not bubble, so listeners are registered in the capture phase to
// catch scrolling inside the app's own scroll containers (the main content area), not just the window.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'scroll', 'touchstart'];
const CHECK_INTERVAL_MS = 1000;
const WARNING_MS = IDLE_WARNING_SECONDS * 1000;

/**
 * Idle auto-logout for a signed-in user. Tracks real input, opens a warning IDLE_WARNING_SECONDS
 * before the idle timeout and calls `onIdle` once the timeout is reached.
 *
 * The idle time is recomputed from the shared last-activity clock every second, and again when the
 * tab becomes visible or focused, rather than relying on one long timer: timers are suspended while
 * a laptop sleeps, and a user coming back after an hour must be signed out at once, not 15 minutes
 * later. The same clock is shared by every tab, so activity in another tab postpones (and closes)
 * this tab's warning.
 *
 * While the warning is open, input in this tab no longer counts — the user answers it explicitly
 * with `staySignedIn` (or `signOutNow`).
 *
 * @param {{ onIdle: () => void, onStay?: () => void }} options
 *   `onIdle` signs the user out; `onStay` is called after "Stay signed in" (extends the session).
 * @returns {{ isWarningOpen: boolean, secondsLeft: number, staySignedIn: () => void, signOutNow: () => void }}
 */
export function useIdleLogout({ onIdle, onStay }) {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(IDLE_WARNING_SECONDS);
  const warningOpenRef = useRef(false);
  const signedOutRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const onStayRef = useRef(onStay);
  useLayoutEffect(() => {
    onIdleRef.current = onIdle;
    onStayRef.current = onStay;
  });

  const signOut = useCallback(() => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    onIdleRef.current?.();
  }, []);

  const check = useCallback(() => {
    if (signedOutRef.current) return;
    const idleMs = getIdleMs();
    if (idleMs >= IDLE_TIMEOUT_MS) {
      signOut();
      return;
    }
    const warn = idleMs >= IDLE_TIMEOUT_MS - WARNING_MS;
    warningOpenRef.current = warn;
    setIsWarningOpen(warn);
    if (warn) setSecondsLeft(Math.max(1, Math.ceil((IDLE_TIMEOUT_MS - idleMs) / 1000)));
  }, [signOut]);

  useEffect(() => {
    // Entering the signed-in app counts as activity — and a stale value left by an earlier session
    // must never sign the user out the moment they arrive.
    markActivity({ force: true });

    const handleActivity = () => {
      if (!warningOpenRef.current) markActivity();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    ACTIVITY_EVENTS.forEach((type) =>
      globalThis.addEventListener(type, handleActivity, { capture: true, passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibility);
    globalThis.addEventListener('focus', check);
    const interval = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((type) =>
        globalThis.removeEventListener(type, handleActivity, { capture: true })
      );
      document.removeEventListener('visibilitychange', handleVisibility);
      globalThis.removeEventListener('focus', check);
      clearInterval(interval);
    };
  }, [check]);

  const staySignedIn = useCallback(() => {
    markActivity({ force: true });
    warningOpenRef.current = false;
    setIsWarningOpen(false);
    onStayRef.current?.();
  }, []);

  return { isWarningOpen, secondsLeft, staySignedIn, signOutNow: signOut };
}
